import { i18n } from "@/modules/App/components/i18n/i18n.client"
import { Pet } from "@/shared/types/global-store"
import { SyncBackPet } from "@/shared/types/lead-api-types"
import { capitalizeFirstLetter } from "@/shared/utils/string"
import {
  differenceInMonths,
  differenceInYears,
  format,
  formatDuration,
  intervalToDuration,
  isAfter,
  parse,
} from "date-fns"
import deLocale from "date-fns/locale/de"
import enLocale from "date-fns/locale/en-GB"
import * as R from "ramda"
import React from "react"

export function generateMonthsOptions(locale: string) {
  return Array.from({ length: 12 }, (_, index) => {
    return {
      label: capitalizeFirstLetter(
        new Date(0, index).toLocaleString(locale, { month: "long" })
      ),
      value: String(index + 1),
    }
  })
}

export function generateYearsOptions() {
  const currentYear = new Date().getFullYear()
  // TODO: change this, I add it ramdomly
  const startYear = 2003

  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) =>
    (startYear + index).toString()
  )

  return years
    .map((year) => ({
      label: year,
      value: year,
    }))
    .reverse()
}

export function getPetBirthday(birthMonth: string, birthYear: string) {
  const birthDate = parse(`${birthMonth}-01-${birthYear}`, "M-d-yyyy", new Date())
  const formattedBirthDate = format(birthDate, "yyyy-MM-dd")

  return { birthDate, formattedBirthDate }
}

export const calculateAgeInYears = (birthDate: Date, currentDate: Date): number => {
  return differenceInYears(currentDate, birthDate)
}

export const calculateAgeInMonths = (birthDate: Date, currentDate: Date): number => {
  return differenceInMonths(currentDate, birthDate)
}

export const isPetTooOld = (birthDate: Date, currentDate: Date): boolean | undefined => {
  const ageInYears = calculateAgeInYears(birthDate, currentDate)

  return ageInYears > 20
}

export const checkIsAfterPetBirthDate = (birthMonth: string, birthYear: string): boolean => {
  const { birthDate } = getPetBirthday(birthMonth, birthYear)
  const currentDate = new Date()
  return isAfter(birthDate, currentDate)
}

export const matchIsValidDate = (
  birthMonth: string,
  birthYear: string
): "idle" | "valid" | "invalid" => {
  if (!birthMonth || !birthYear) {
    return "idle"
  }
  const currentDate = new Date()
  const { birthDate } = getPetBirthday(birthMonth, birthYear)

  if (checkIsAfterPetBirthDate(birthMonth, birthYear)) {
    return "invalid"
  }

  if (isPetTooOld(birthDate, currentDate)) {
    return "invalid"
  }

  return "valid"
}

export function parsePetBirthday(date: string): Date {
  const parsedDate = parse(date, "yyyy-MM-dd", new Date())
  parsedDate.setHours(0, 0, 0, 0)
  parsedDate.setDate(1)
  return parsedDate
}

const localeMap: Record<string, Locale> = {
  en: enLocale,
  "en-GB": enLocale,
  de: deLocale,
  "de-DE": deLocale,
}

export function getYearAndMonth(birthMonth: string, birthYear: string, locale = "en"): string {
  const currentDate = new Date()
  currentDate.setDate(1)

  const birthDate = parse(`${birthMonth}-01-${birthYear}`, "M-d-yyyy", currentDate)
  const diffInMonths = differenceInMonths(currentDate, birthDate)

  if (diffInMonths < 1) {
    return locale.startsWith("de") ? "0 Monate" : "0 months"
  }

  const dateFnsLocale = localeMap[locale] ?? enLocale

  return formatDuration(intervalToDuration({ start: birthDate, end: currentDate }), {
    format: ["years", "months"],
    locale: dateFnsLocale,
    delimiter: locale.startsWith("de") ? " und " : " and ",
  })
}

//@ts-ignore
const petOrder: string[] = i18n.t("pet-order", {
  ns: "pet-name-page",
  returnObjects: true,
})

export function getPetOrder(index: number) {
  if (index >= 1 && index <= petOrder.length) {
    return petOrder[index - 1]
  }

  return `${index + 1}${i18n.t("th", {
    ns: "pet-name-page",
  })}`
}
export type AgeSegment = "future" | "0-12m" | "1y-8y" | ">20y"

function startOfMonthNoTime(d: Date) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  x.setDate(1)
  return x
}

function getAgeSegmentByDates(birthDate: Date, currentDate: Date): AgeSegment {
  // normalise
  const normBirth = startOfMonthNoTime(birthDate)
  const normNow = startOfMonthNoTime(currentDate)

  if (isAfter(normBirth, normNow)) return "future"

  const months = calculateAgeInMonths(normBirth, normNow)
  const years = calculateAgeInYears(normBirth, normNow)

  if (years > 20) return ">20y"
  if (months < 12) return "0-12m"
  return "1y-8y"
}

export function getAgeSegment(birthMonth: string, birthYear: string): AgeSegment {
  const { birthDate } = getPetBirthday(birthMonth, birthYear)
  return getAgeSegmentByDates(birthDate, new Date())
}

export type AlertVariant = "error" | "info" | "default"

export function getPetBirthdateCardContent(
  birthMonth: string,
  birthYear: string,
  petSex: Pet["sexe"],
  petName: Pet["name"]
): { title: string | null; description: React.ReactNode; variant: AlertVariant } {
  const segment = getAgeSegment(birthMonth, birthYear)

  if (segment === "future") {
    return {
      title: null,
      description: i18n.t("pet-birthdate-errors.future-birthdate", { ns: "pet-birthdate-page" }),
      variant: "error",
    }
  }

  if (segment === ">20y") {
    return {
      title: null,
      description: i18n.t("pet-birthdate-errors.pet-is-too-old", { ns: "pet-birthdate-page" }),
      variant: "error",
    }
  }

  if (segment === "0-12m") {
    return {
      title: i18n.t(`pet-birthdate-info-card-title-two-months-one-year-${petSex.toLowerCase()}`, {
        ns: "pet-birthdate-page",
        age: getYearAndMonth(birthMonth, birthYear, i18n.language),

      }),
      description: i18n.t(`pet-birthdate-info-card-two-months-one-year-${petSex.toLowerCase()}`, {
        ns: "pet-birthdate-page",
        pet_name: petName,
      }),
      variant: "info",
    }
  }

  return {
    title: i18n.t(`pet-birthdate-info-card-title-${petSex.toLowerCase()}`, {
      ns: "pet-birthdate-page",
      age: getYearAndMonth(birthMonth, birthYear, i18n.language),

    }),
    description: i18n.t(`pet-birthdate-info-card-subtitle-${petSex.toLowerCase()}`, {
      ns: "pet-birthdate-page",
      pet_name: petName,
    }),
    variant: "info",
  }
}

export function getPetById(
  pets: Partial<SyncBackPet>[],
  petId?: string | null
): Partial<SyncBackPet> | null {
  if (!pets || !petId) {
    return null
  }
  // @ts-expect-error this is an expected error as id isn't in backend type
  return R.find(R.propEq(petId, "id"))(pets || [])
}

export function getPetIndex(pets: Partial<SyncBackPet>[], petId?: string | null): number {
  // @ts-expect-error this is an expected error as id isn't in backend type
  return R.findIndex(R.propEq(petId, "id"))(pets || [])
}

